import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { useToast } from '../../components/Toast';
import extractApiError from '../../utils/apiError';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { logError } from '../../utils/logError';
import { parseTutarTR, formatTutarTR } from '../../utils/para';
import { normalizeForSearch, searchMatchPre } from '../../utils/searchMatch';
import SearchableSelect from '../../components/SearchableSelect';
import EnterNavForm from '../../components/EnterNavForm';

// PR-B (PB4) — Satış İrsaliyesi oluşturma formu.
// Müşteri (SearchableSelect, /api/cari?tip=MUSTERI) + satan firma (default TORUNA_KASAP)
// + çok-kalem (MalzemeAutocomplete) + canlı KDV/toplam.
// kaynak_lokasyon_id GÖNDERİLMEZ — backend üretim lokasyonunu çözer (F5).
// POST /api/satis-irsaliyeleri body: {cari_id, satan_firma_id, tarih, notlar, kalemler:[…]}

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const BIRIM_OPTIONS = ['KG', 'ADET', 'LT', 'PAKET', 'KOLI', 'PORSIYON'];

// ==================== MALZEME AUTOCOMPLETE (portal) ====================
// FiyatGirisiPage deseninden uyarlandı: dropdown portal ile body'ye render edilir
// (tablo overflowX:auto kırpmasın) + klavye navigasyonu.
const MalzemeAutocomplete = ({ malzemeler, value, onSelect }) => {
  const [query, setQuery] = useState('');
  const [showList, setShowList] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [coords, setCoords] = useState(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (value) {
      const m = malzemeler.find((mal) => String(mal.id) === String(value));
      if (m) setQuery(m.ad);
    } else {
      setQuery('');
    }
  }, [value, malzemeler]);

  useEffect(() => { setActiveIndex(-1); }, [filtered]);

  useEffect(() => {
    if (!showList) { setCoords(null); return undefined; }
    const recompute = () => {
      if (!inputRef.current) return;
      const r = inputRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom, left: r.left, width: r.width });
    };
    recompute();
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [showList]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inWrapper = wrapperRef.current && wrapperRef.current.contains(e.target);
      const inList = listRef.current && listRef.current.contains(e.target);
      if (!inWrapper && !inList) setShowList(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onSelect(null); // seçimi temizle
    if (val.length >= 1) {
      const lower = normalizeForSearch(val);
      const results = malzemeler.filter((m) =>
        searchMatchPre(m.ad, lower) || (m.kod && searchMatchPre(m.kod, lower)),
      );
      setFiltered(results.slice(0, 15));
      setShowList(true);
    } else {
      setFiltered([]);
      setShowList(false);
    }
  };

  const handleSelect = (m) => {
    setQuery(m.ad);
    setShowList(false);
    onSelect(m);
  };

  const handleFocus = () => {
    if (!value && query.length >= 1) {
      setShowList(true);
    } else if (!value) {
      setFiltered(malzemeler.slice(0, 15));
      setShowList(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showList && filtered.length > 0) setShowList(true);
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      // EnterNavForm ile uyum: liste açık ve sonuç varsa Enter highlighted-or-first seçer
      // ve preventDefault eder (form alan-gezme/satır-ekleme yapmaz). Seçim yoksa serbest bırak.
      if (showList && filtered.length > 0) {
        e.preventDefault();
        handleSelect(filtered[activeIndex >= 0 ? activeIndex : 0]);
      }
    } else if (e.key === 'Escape') {
      setShowList(false);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder="Malzeme ara..."
        style={{
          width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
          borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box',
          background: value ? '#f0fff0' : 'white',
        }}
      />
      {showList && filtered.length > 0 && coords && ReactDOM.createPortal(
        <div
          ref={listRef}
          style={{
            position: 'fixed', top: coords.top, left: coords.left, width: coords.width,
            background: colors.surface, border: `1px solid ${colors.border}`,
            borderRadius: '0 0 6px 6px', boxShadow: shadows.md, maxHeight: '220px',
            overflowY: 'auto', zIndex: 3000,
          }}
        >
          {filtered.map((m, idx) => (
            <div
              key={m.id}
              onMouseDown={() => handleSelect(m)}
              style={{
                padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                fontSize: '13px', display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', background: idx === activeIndex ? colors.infoLight : 'white',
              }}
              onMouseEnter={() => setActiveIndex(idx)}
            >
              <span><strong>{m.ad}</strong></span>
              <span style={{ color: colors.textTertiary, fontSize: '11px' }}>
                {m.birim} {m.fiyat > 0 ? `| ${m.fiyat.toFixed(2)} TL` : ''}
              </span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
};

const emptyKalem = () => ({ malzeme_id: '', miktar: '', birim: 'KG', birim_fiyat: '', kdv_orani: '10' });

const labelStyle = { display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#555' };
const inputStyle = {
  width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
  borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box',
};
const thStyle = {
  padding: '10px 12px', textAlign: 'left', fontSize: '13px', fontWeight: 'bold',
  color: '#555', borderBottom: '2px solid #dee2e6',
};
const tdStyle = { padding: '8px 12px', fontSize: '14px', verticalAlign: 'middle' };

const SatisIrsaliyesiOlusturPage = () => {
  const { user } = useAuth();
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_SATIS, user.effectivePermissions);
  // İrsaliye oluşturma depo dahil CREATE_SATIS_IRSALIYE yetkisiyle yapılabilir (onayla/iptal MANAGE_SATIS'te).
  const canCreate = (user && hasPermission(user.role, PERMISSIONS.CREATE_SATIS_IRSALIYE, user.effectivePermissions)) || canManage;
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const duzenleId = searchParams.get('duzenle');
  const isEdit = Boolean(duzenleId);

  const [cariler, setCariler] = useState([]);
  const [firmalar, setFirmalar] = useState([]);
  const [malzemeler, setMalzemeler] = useState([]);

  const [cariId, setCariId] = useState('');
  const [satanFirmaId, setSatanFirmaId] = useState('');
  const [tarih, setTarih] = useState(new Date().toISOString().split('T')[0]);
  const [notlar, setNotlar] = useState('');
  const [kalemler, setKalemler] = useState([emptyKalem()]);
  const [saving, setSaving] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [irsaliyeNo, setIrsaliyeNo] = useState('');

  const token = localStorage.getItem('access_token');

  useEffect(() => {
    fetch(`${API}/api/cari?tip=MUSTERI`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setCariler(Array.isArray(data) ? data : []))
      .catch(() => setCariler([]));
    fetch(`${API}/api/malzemeler`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setMalzemeler(Array.isArray(data) ? data : []))
      .catch(() => setMalzemeler([]));
    fetch(`${API}/api/firmalar`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setFirmalar(list);
        // Satan firma default TORUNA_KASAP (kod ile bul → id'sini seç)
        const tk = list.find((f) => f.kod === 'TORUNA_KASAP');
        if (tk && !isEdit) setSatanFirmaId(String(tk.id));
      })
      .catch(() => setFirmalar([]));
  }, [token, isEdit]);

  // Düzenleme modu: mevcut TASLAK irsaliyeyi forma yükle.
  // Aynı sayfa /satislar/irsaliye/yeni?duzenle=ID ile açılır; yeni route gerekmez.
  useEffect(() => {
    if (!isEdit || !duzenleId) return undefined;

    let cancelled = false;

    const loadIrsaliye = async () => {
      setEditLoading(true);
      try {
        const res = await fetch(`${API}/api/satis-irsaliyeleri/${duzenleId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          toast.error(extractApiError(data.detail));
          navigate('/satislar/irsaliyeler');
          return;
        }

        if (data.durum !== 'TASLAK') {
          toast.error('Yalnız TASLAK irsaliyeler düzenlenebilir.');
          navigate('/satislar/irsaliyeler');
          return;
        }

        if (cancelled) return;

        setIrsaliyeNo(data.irsaliye_no || '');
        setCariId(data.cari_id ? String(data.cari_id) : '');
        setSatanFirmaId(data.satan_firma_id ? String(data.satan_firma_id) : '');
        setTarih((data.tarih || '').slice(0, 10) || new Date().toISOString().split('T')[0]);
        setNotlar(data.notlar || '');

        const mevcutKalemler = Array.isArray(data.kalemler) ? data.kalemler : [];
        setKalemler(
          mevcutKalemler.length > 0
            ? mevcutKalemler.map((k) => ({
                malzeme_id: k.malzeme_id ? String(k.malzeme_id) : '',
                miktar: formatTutarTR(k.miktar || 0),
                birim: k.birim || 'KG',
                birim_fiyat: formatTutarTR(k.birim_fiyat || 0),
                kdv_orani: String(k.kdv_orani ?? 0),
              }))
            : [emptyKalem()]
        );
      } catch (err) {
        logError('İrsaliye düzenleme verisi yükleme hatası:', err);
        toast.error('İrsaliye bilgileri alınamadı');
      } finally {
        if (!cancelled) setEditLoading(false);
      }
    };

    loadIrsaliye();
    return () => { cancelled = true; };
  }, [isEdit, duzenleId, token, navigate, toast]);

  const handleKalemChange = useCallback((index, field, value) => {
    setKalemler((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const handleMalzemeSelect = useCallback((index, malzeme) => {
    setKalemler((prev) => {
      const updated = [...prev];
      if (!malzeme) {
        updated[index] = { ...updated[index], malzeme_id: '' };
        return updated;
      }
      updated[index] = {
        ...updated[index],
        malzeme_id: malzeme.id,
        birim: malzeme.birim || updated[index].birim || 'KG',
        birim_fiyat: updated[index].birim_fiyat || (malzeme.fiyat > 0 ? formatTutarTR(malzeme.fiyat) : ''),
        kdv_orani: (malzeme.kdv_orani !== undefined && malzeme.kdv_orani !== null)
          ? String(malzeme.kdv_orani)
          : updated[index].kdv_orani,
      };
      return updated;
    });
  }, []);

  const handleKalemEkle = () => setKalemler((prev) => [...prev, emptyKalem()]);
  const handleKalemSil = (index) => {
    setKalemler((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  // Satır bazlı KDV matematiği: net = miktar × birim_fiyat; kdv = net × kdv/100; brut = net + kdv
  const satirNet = (k) => (parseTutarTR(k.miktar) || 0) * (parseTutarTR(k.birim_fiyat) || 0);
  const satirKdv = (k) => satirNet(k) * ((parseFloat(k.kdv_orani) || 0) / 100);
  const satirBrut = (k) => satirNet(k) + satirKdv(k);

  const toplamNet = kalemler.reduce((s, k) => s + satirNet(k), 0);
  const toplamKdv = kalemler.reduce((s, k) => s + satirKdv(k), 0);
  const toplamBrut = toplamNet + toplamKdv;

  const handleKaydet = async () => {
    if (!cariId) { toast.error('Müşteri seçiniz'); return; }
    if (!satanFirmaId) { toast.error('Satan firma seçiniz'); return; }

    const validKalemler = kalemler.filter(
      (k) => k.malzeme_id && (parseTutarTR(k.miktar) || 0) > 0 && (parseTutarTR(k.birim_fiyat) || 0) > 0,
    );
    if (validKalemler.length === 0) {
      toast.error('En az bir geçerli kalem giriniz (malzeme, miktar, birim fiyat)');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        cari_id: parseInt(cariId, 10),
        satan_firma_id: parseInt(satanFirmaId, 10),
        tarih,
        notlar: notlar || null,
        // kaynak_lokasyon_id GÖNDERİLMEZ — mevcut taslakta korunur,
        // yeni belgede backend üretim lokasyonunu çözer (F5).
        kalemler: validKalemler.map((k) => ({
          malzeme_id: parseInt(k.malzeme_id, 10),
          miktar: parseTutarTR(k.miktar),
          birim: k.birim || null,
          birim_fiyat: parseTutarTR(k.birim_fiyat),
          kdv_orani: parseFloat(k.kdv_orani) || 0,
        })),
      };

      const url = isEdit
        ? `${API}/api/satis-irsaliyeleri/${duzenleId}`
        : `${API}/api/satis-irsaliyeleri`;

      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(
          isEdit
            ? `İrsaliye güncellendi: ${data.irsaliye_no || irsaliyeNo || ''}`
            : `İrsaliye oluşturuldu: ${data.irsaliye_no || ''}`
        );
        navigate('/satislar/irsaliyeler');
      } else {
        toast.error(extractApiError(data.detail));
      }
    } catch (err) {
      logError('İrsaliye kaydetme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Header title={isEdit ? "Satış İrsaliyesi Düzenle" : "Yeni Satış İrsaliyesi"} />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: colors.textPrimary }}>{isEdit ? `Satış İrsaliyesi Düzenle${irsaliyeNo ? ` — ${irsaliyeNo}` : ""}` : "Yeni Satış İrsaliyesi"}</h2>
          <button
            onClick={() => navigate('/satislar/irsaliyeler')}
            style={{ padding: '10px 20px', background: colors.surface, color: colors.textPrimary, border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', whiteSpace: 'nowrap' }}
          >
            ← Geri
          </button>
        </div>

        {/* Enter ile alan-gezme: alanlar arası ilerle, kalem birim fiyatında yeni/sonraki satır.
            onSubmit BİLİNÇLİ OLARAK BAĞLANMADI — kaydetme stok düşümü yapan geri-alınamaz sevk
            (Mal Kabul onayı deseni gibi); Enter kazara irsaliye oluşturmasın. */}
        <EnterNavForm onRowAdd={handleKalemEkle}>

        {/* Belge bilgileri */}
        <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '24px', boxShadow: shadows.md, marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 20px', color: styles.secondary }}>Belge Bilgileri</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
            {/* Müşteri */}
            <div>
              <label style={labelStyle}>Müşteri *</label>
              <SearchableSelect
                options={cariler.map((c) => ({ value: String(c.id), label: c.unvan }))}
                value={cariId}
                onChange={setCariId}
                placeholder="Müşteri ara…"
              />
            </div>

            {/* Satan firma */}
            <div>
              <label style={labelStyle}>Satan Firma *</label>
              <select value={satanFirmaId} onChange={(e) => setSatanFirmaId(e.target.value)} style={inputStyle}>
                <option value="">Firma Seçiniz</option>
                {firmalar.filter((f) => f.aktif !== false).map((f) => (
                  <option key={f.id} value={String(f.id)}>{f.ad}</option>
                ))}
              </select>
            </div>

            {/* Tarih */}
            <div>
              <label style={labelStyle}>Tarih *</label>
              <input type="date" value={tarih} onChange={(e) => setTarih(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div style={{ marginTop: '15px' }}>
            <label style={labelStyle}>Notlar</label>
            <input
              type="text"
              value={notlar}
              onChange={(e) => setNotlar(e.target.value)}
              placeholder="İrsaliye notu (opsiyonel)…"
              style={inputStyle}
            />
          </div>
        </div>

        {/* Kalemler */}
        <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '24px', boxShadow: shadows.md, marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 10px', color: styles.secondary }}>Kalemler</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '15px' }}>
              <thead>
                <tr style={{ background: colors.backgroundAlt }}>
                  <th style={{ ...thStyle, width: '35px' }}>#</th>
                  <th style={thStyle}>Malzeme *</th>
                  <th style={{ ...thStyle, width: '90px' }}>Miktar *</th>
                  <th style={{ ...thStyle, width: '95px' }}>Birim</th>
                  <th style={{ ...thStyle, width: '120px' }}>Birim Fiyat *</th>
                  <th style={{ ...thStyle, width: '80px' }}>KDV %</th>
                  <th style={{ ...thStyle, width: '110px' }}>Net</th>
                  <th style={{ ...thStyle, width: '110px' }}>Brüt</th>
                  <th style={{ ...thStyle, width: '40px' }}></th>
                </tr>
              </thead>
              <tbody>
                {kalemler.map((k, idx) => (
                  <tr key={idx} data-enter-row style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ ...tdStyle, color: colors.textTertiary }}>{idx + 1}</td>
                    <td style={tdStyle}>
                      <MalzemeAutocomplete
                        malzemeler={malzemeler}
                        value={k.malzeme_id}
                        onSelect={(m) => handleMalzemeSelect(idx, m)}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="text"
                        value={k.miktar}
                        onChange={(e) => handleKalemChange(idx, 'miktar', e.target.value)}
                        placeholder="0"
                        style={{ ...inputStyle, textAlign: 'right' }}
                      />
                    </td>
                    <td style={tdStyle}>
                      <select
                        value={k.birim}
                        onChange={(e) => handleKalemChange(idx, 'birim', e.target.value)}
                        style={inputStyle}
                      >
                        {(BIRIM_OPTIONS.includes(k.birim) ? BIRIM_OPTIONS : [k.birim, ...BIRIM_OPTIONS]).map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="text"
                        data-enter-nav="row-add"
                        value={k.birim_fiyat}
                        onChange={(e) => handleKalemChange(idx, 'birim_fiyat', e.target.value)}
                        placeholder="0,00"
                        style={{ ...inputStyle, textAlign: 'right' }}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={k.kdv_orani}
                        onChange={(e) => handleKalemChange(idx, 'kdv_orani', e.target.value)}
                        style={{ ...inputStyle, textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{formatTutarTR(satirNet(k))}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>{formatTutarTR(satirBrut(k))}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <button
                        onClick={() => handleKalemSil(idx)}
                        disabled={kalemler.length <= 1}
                        title="Kalemi sil"
                        style={{
                          background: 'none', border: 'none',
                          cursor: kalemler.length <= 1 ? 'not-allowed' : 'pointer',
                          color: kalemler.length <= 1 ? colors.textTertiary : '#d32f2f',
                          fontSize: '18px', fontWeight: 'bold',
                        }}
                      >×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            onClick={handleKalemEkle}
            style={{ padding: '8px 16px', background: colors.backgroundAlt, color: colors.textPrimary, border: `1px dashed ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
          >
            + Kalem Ekle
          </button>

          {/* Toplam */}
          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ minWidth: '280px', fontSize: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                <span style={{ color: colors.textSecondary }}>Net Toplam:</span>
                <span style={{ fontFamily: 'monospace' }}>{formatTutarTR(toplamNet)} TL</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                <span style={{ color: colors.textSecondary }}>KDV Toplam:</span>
                <span style={{ fontFamily: 'monospace' }}>{formatTutarTR(toplamKdv)} TL</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: `1px solid ${colors.border}`, marginTop: '4px', fontWeight: 'bold', fontSize: '16px' }}>
                <span>Genel Toplam:</span>
                <span style={{ fontFamily: 'monospace', color: styles.primary }}>{formatTutarTR(toplamBrut)} TL</span>
              </div>
            </div>
          </div>
        </div>

        </EnterNavForm>

        {/* Aksiyonlar */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => navigate('/satislar/irsaliyeler')}
            style={{ padding: '12px 24px', background: colors.surface, color: colors.textPrimary, border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
          >
            İptal
          </button>
          <button
            onClick={handleKaydet}
            disabled={saving || editLoading || !canCreate}
            title={!canCreate ? 'Bu işlem için yetkiniz yok' : undefined}
            style={{
              padding: '12px 32px', border: 'none', borderRadius: radius.sm,
              background: (saving || editLoading || !canCreate) ? '#90caf9' : styles.primary, color: 'white',
              cursor: (saving || editLoading || !canCreate) ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '14px',
            }}
          >
            {saving ? 'Kaydediliyor…' : (editLoading ? 'Yükleniyor…' : (isEdit ? 'Değişiklikleri Kaydet' : 'Kaydet'))}
          </button>
        </div>

      </div>
    </div>
  );
};

export default SatisIrsaliyesiOlusturPage;
